import { useQuery } from '@tanstack/react-query';

import {
  CollectionListApiResponse,
  CollectionFilterApiResponse,
} from '../apis';

import { useProfile } from '@/shared/hooks';
import { clientAxios } from '@/shared/api';
import { ApiResponse } from '@/shared/types';
import { convertCollectionListItem } from '@/shared/utils';

export const useMyCollectionsQuery = () => {
  const { profile } = useProfile();

  return useQuery({
    enabled: !!profile?.yozmitProfileId,
    queryKey: ['getMyCollectionsApi'],
    queryFn: async ({ signal }) => {
      const { data } = await clientAxios.get<
        ApiResponse<CollectionListApiResponse>
      >('/getMyCollectionsApi/', { signal });

      return convertCollectionListItem(data.data.results);
    },
  });
};

export const useCollectionTagQuery = () => {
  return useQuery({
    queryKey: ['collectionTagApi'],
    queryFn: async () => {
      const { data } =
        await clientAxios.get<ApiResponse<CollectionFilterApiResponse>>(
          '/collectionTagApi/'
        );

      const defaultTag = {
        tag: 'popular',
        name: '인기',
        filter: ['popular'],
      };

      const transformedArray = Object.keys(data.data).map((key) => ({
        tag: key,
        name: data.data[key].name,
        filter: data.data[key].filter,
      }));

      return [defaultTag, ...transformedArray];
    },
  });
};

export const useCollectionSearchQuery = (params: {
  page?: number;
  chip?: string[];
  search?: string;
}) => {
  const enabled = !!params.search || !!params.chip;

  return useQuery({
    enabled,
    queryKey: ['collectionSearchApi', params],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<CollectionListApiResponse>
      >('/collectionSearchApi/', { params });

      return {
        totalCount: data.data.count,
        maxPage: Math.ceil(data.data.count / 16),
        list: convertCollectionListItem(data.data.results),
      };
    },
  });
};

export const useCollectionPopularQuery = ({
  enabled,
}: {
  enabled: boolean;
}) => {
  return useQuery({
    enabled,
    queryKey: ['collectionSearchApi'],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<CollectionListApiResponse>
      >('/collectionSearchApi/', {
        params: {
          pageSize: 4,
          chip: ['popular'],
        },
      });

      return {
        totalCount: data.data.count,
        maxPage: Math.ceil(data.data.count / 16),
        list: convertCollectionListItem(data.data.results),
      };
    },
  });
};

export const useRecommendMyCollectionQuery = ({
  enabled,
}: {
  enabled: boolean;
}) => {
  return useQuery({
    enabled,
    retry: false,
    queryKey: ['recommendMyCollectionApi'],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<CollectionListApiResponse>
      >('/recommendMyCollectionApi/');

      const privateCollection = convertCollectionListItem(data.data.results)
        .filter(({ isPublic }) => !isPublic)
        .slice(0, 5);

      return privateCollection;
    },
  });
};
